import styled, { css } from 'styled-components';

// Base styles that can be shared between components
const baseInputStyles = css`
  width: 100%;
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #ff6320;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

// Form container
export const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

// Form section (for grouping related fields)
export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

// Label
export const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  color: #1b294b;
  font-weight: 500;

  &.required::after {
    content: '*';
    color: #dc3545;
    margin-left: 4px;
  }
`;

// Input
export const Input = styled.input`
  ${baseInputStyles}
  height: 40px;

  &[type="color"] {
    height: 40px;
    padding: 4px;
    cursor: pointer;
  }

  &[type="file"] {
    padding: 6px;
    cursor: pointer;
  }
`;

// Textarea
export const TextArea = styled.textarea`
  ${baseInputStyles}
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
`;

// Select
export const Select = styled.select`
  ${baseInputStyles}
  height: 40px;
  background-color: white;
  cursor: pointer;
  appearance: none;
  padding-right: 32px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
`;

// Checkbox container
export const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Checkbox input
export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #ff6320;
`;

// Radio container
export const RadioContainer = styled.div`
  display: flex;
  gap: 16px;
`;

// Radio group
export const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Radio input
export const Radio = styled.input.attrs({ type: 'radio' })`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #ff6320;
`;

// Helper text
export const HelperText = styled.span<{ error?: boolean }>`
  font-size: 12px;
  color: ${props => props.error ? '#dc3545' : '#6c757d'};
  margin-top: 4px;
`;

// Error message
export const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
`;

// Form actions container
export const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

// Button base styles
const buttonBase = css`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Primary button
export const PrimaryButton = styled.button`
  ${buttonBase}
  background-color: #ff6320;
  color: white;

  &:hover:not(:disabled) {
    background-color: #e0551c;
  }
`;

// Secondary button
export const SecondaryButton = styled.button`
  ${buttonBase}
  background-color: #e9ecef;
  color: #1b294b;

  &:hover:not(:disabled) {
    background-color: #dee2e6;
  }
`;

// Toggle switch
export const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #e9ecef;
    transition: 0.4s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: "";
      height: 16px;
      width: 16px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: 0.4s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background-color: #ff6320;
  }

  input:checked + span:before {
    transform: translateX(24px);
  }
`;

// Field group (horizontal layout)
export const FieldGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// Form divider
export const FormDivider = styled.hr`
  border: none;
  border-top: 1px solid #e9ecef;
  margin: 24px 0;
`;

// Form legend
export const Legend = styled.legend`
  font-size: 16px;
  font-weight: 600;
  color: #1b294b;
  margin-bottom: 16px;
`;